/**
 * ADR-0185 (seam per Addendum 3) — the machine-scheduling board view:
 * everything that knows this template schedules machines. Lanes lead (the
 * Next-Up rail moved to its own tab — Addendum 5, owner-ruled) and carry the
 * block faces (capacity shading, pins, promise badges, estimated costs); the
 * graded change sheet scrolls BELOW the lanes (Phase A, owner-ruled: one
 * view, no tabs anywhere in the loop); the who's-out/what's-down panel
 * asserts disruptions (view-side because it encodes the domain's subject
 * vocabulary — its writes stay shell-owned, arriving as callbacks).
 *
 * Phase B — the Refine loop, on the same view (design record 2026-09-04,
 * converged via the mockup): the working row is the REFINED plan (the
 * standing plan until a move lands; the Plex schedule after "Reset to Plex
 * schedule"; the last replan after Replan); expanding adds "Optimized" and
 * "Plex" beneath. Click a job → the move popover (never a modal) → the
 * pending-moves table above the sheet → Replan (always a full restructure
 * around the pins) → tier ① on the sheet → Make this the plan. Every write
 * is a shell callback. In scenario mode (What-if) the refine loop steps
 * aside and the Phase A frame shows as built.
 *
 * Expansion: one, several, or all machines (Phase A), remembered per browser
 * per model; the Next-up tab's jump (initialFocus) expands its machine on
 * top of what was remembered.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "../../../States.js";
import { useConfirm } from "../../../ConfirmDialog.js";
import type { BoardViewProps } from "../../viewRegistry.js";
import type { BoardLane, BoardLaneJob, BoardProposalView, Placement, PlacementKind } from "../../boardTypes.js";
import { Lanes } from "./Lanes.js";
import { ChangeSheet } from "./ChangeSheet.js";
import { DisruptionsPanel } from "./DisruptionsPanel.js";
import { MovePopover } from "./MovePopover.js";
import { PendingMoves } from "./PendingMoves.js";
import { RefineDock } from "./RefineDock.js";
import { ReplanStrip } from "./ReplanStrip.js";
import { FindBox } from "./FindBox.js";
import { findIndexOf, findMatches, measuredCountsOf, stepFind } from "./laneMath.js";
import { planJobsOf } from "./refineMath.js";
import {
  expandedSetFor,
  loadExpandedLanes,
  saveExpandedLanes,
  toggleMachine,
  type ExpandedLanes,
} from "./expandState.js";

/**
 * The shipped machine-scheduling template's seed id (a fixed literal in the
 * hub's seed migration, identical in every environment). An internal key only
 * — never rendered.
 */
export const MACHINE_SCHEDULING_TEMPLATE_ID = "a0183001-0000-4000-8000-000000000001";

type PopState = {
  job: BoardLaneJob | { job: string; part: string | null };
  machine: string;
  anchor: HTMLElement;
  initialKind?: PlacementKind;
  /** Amendment 4 — the clicked lane row's served approved set (null when there is none to offer from). */
  approved?: BoardLaneJob["approved_machines"];
};

/**
 * The Refined row's lanes for a 'plex' basis: the standing plan's Plex track
 * drawn as the working row (the schedule import IS the working copy until a
 * replan lands). Jobs keep their book machine and Plex start.
 */
function plexAsWorking(standing: BoardProposalView): BoardLane[] {
  return standing.scheduled_lanes.map((l) => ({ machine: l.machine, jobs: l.jobs }));
}

export function MachineSchedulingView(props: BoardViewProps) {
  const confirm = useConfirm();
  const modelId = props.payload.model.id;
  // The remembered choice, plus the Next-up jump's machine when one arrived.
  const [expandState, setExpandState] = useState<ExpandedLanes | null>(() => {
    const stored = loadExpandedLanes(modelId);
    if (!props.initialFocus) return stored;
    if (stored?.all) return stored;
    const machines = new Set(stored?.machines ?? []);
    machines.add(props.initialFocus);
    return { all: false, machines: [...machines] };
  });
  const [pop, setPop] = useState<PopState | null>(null);
  // Find (iteration 1). The ACTIVE MATCH is held as a job id, never an index:
  // a replan reorders the lanes, and an index carried across one lands on a
  // different job with no tell. Holding the id makes the highlight survive a
  // replan by construction — it re-attaches to the same job or to nothing.
  const [findQuery, setFindQuery] = useState("");
  const [findJob, setFindJob] = useState<string | null>(null);

  const { payload, view, scenario } = props;
  const standing = payload.standing;
  const canEdit = payload.can_edit;
  const refine = payload.refine;
  const refineContext = !scenario && canEdit && !!standing;

  const disruptionsCard = payload.template.cards.find((c) => c.block_kind === "disruptions");
  const accepts = disruptionsCard?.accepts ?? [];
  const machines = (standing?.lanes ?? []).map((l) => l.machine);
  const planJobs = (standing?.lanes ?? []).flatMap((l) => l.jobs.map((j) => ({ job: j.job, part: j.part })));

  // The Refined row: what the working copy draws. Outside refine context the
  // Phase A frame stands (the rendered view's own lanes).
  const working: { lanes: BoardLane[]; sheet: BoardProposalView["change_sheet"]; label: string; target: "Optimized" | "Refined" } = useMemo(() => {
    if (!refineContext || !standing) {
      return { lanes: view?.lanes ?? [], sheet: view?.change_sheet ?? null, label: "Optimized", target: "Optimized" };
    }
    if (refine.basis === "refined" && refine.refined) {
      return { lanes: refine.refined.lanes, sheet: refine.refined.change_sheet, label: "Refined ↑", target: "Refined" };
    }
    if (refine.basis === "plex") {
      return { lanes: plexAsWorking(standing), sheet: null, label: "Refined ↑", target: "Refined" };
    }
    return { lanes: standing.lanes, sheet: standing.change_sheet, label: "Refined ↑", target: "Refined" };
  }, [refineContext, standing, view, refine]);

  const workingPlanJobs = useMemo(() => planJobsOf({ lanes: working.lanes }), [working.lanes]);
  const pendingJobs = useMemo(() => new Set(refine.pending_moves.map((m) => m.job)), [refine.pending_moves]);
  const viewMachines = useMemo(() => working.lanes.map((l) => l.machine), [working.lanes]);
  const expandedMachines = useMemo(() => expandedSetFor(expandState, viewMachines), [expandState, viewMachines]);

  const applyExpand = (next: ExpandedLanes) => {
    setExpandState(next);
    saveExpandedLanes(modelId, next);
  };

  // ---- Find (Phase B iteration 1, owner's pick 2a) ------------------------
  // Searched across all three tracks, so a job Plex holds that the plan does
  // not is still findable. Never a filter: every lane stays on screen.
  const findLanes = useMemo(
    () => [...working.lanes, ...(standing?.lanes ?? []), ...(standing?.scheduled_lanes ?? [])],
    [working.lanes, standing]
  );
  const matches = useMemo(() => findMatches(findLanes, findQuery), [findLanes, findQuery]);
  // Re-anchor whenever the match set moves under the active job (a replan, or
  // the query narrowing): land on the first match rather than on nothing.
  const activeFind = matches.length === 0 ? null : (matches[findIndexOf(matches, findJob)] ?? matches[0]!).job;
  const findPosition = activeFind ? findIndexOf(matches, activeFind) + 1 : 0;
  const activeMachine = activeFind ? matches.find((m) => m.job === activeFind)?.machine ?? null : null;

  // Jumping to a match expands its machine (the other two tracks only exist
  // when the lane is open) and scrolls the lane into view. Expanding here is
  // additive — it never collapses what the planner had open, and it is
  // remembered like any other expansion, because he is now working there.
  const lastJumped = useRef<string | null>(null);
  useEffect(() => {
    if (!activeFind || !activeMachine) {
      lastJumped.current = null;
      return;
    }
    const key = `${activeFind}@${activeMachine}`;
    if (lastJumped.current === key) return;
    lastJumped.current = key;
    setExpandState((prev) => {
      if (prev?.all || prev?.machines.includes(activeMachine)) return prev;
      const next: ExpandedLanes = { all: false, machines: [...(prev?.machines ?? []), activeMachine] };
      saveExpandedLanes(modelId, next);
      return next;
    });
    // After the expansion paints, bring the lane onto the screen.
    const id = window.requestAnimationFrame(() => {
      document.getElementById(`lane-${activeMachine}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeFind, activeMachine, modelId]);

  const clearFind = () => {
    setFindQuery("");
    setFindJob(null);
  };

  const scenarioHighlight =
    scenario && view?.kind === "what_if" && view.versus_previous
      ? new Set([...view.versus_previous.jobs_moved.map((m) => m.job), ...view.versus_previous.jobs_added])
      : undefined;

  // ---- refine gestures (every write goes through the shell) ---------------

  async function queueMove(move: Placement): Promise<string | null> {
    const others = refine.pending_moves.filter((m) => m.job !== move.job);
    const existingIdx = refine.pending_moves.findIndex((m) => m.job === move.job);
    const next = existingIdx >= 0 ? refine.pending_moves.map((m, i) => (i === existingIdx ? move : m)) : [...others, move];
    return props.refine.setMoves(next);
  }

  async function removeMove(move: Placement) {
    await props.refine.setMoves(refine.pending_moves.filter((m) => m.job !== move.job));
  }

  async function switchHow(move: Placement, kind: PlacementKind) {
    const next: Placement = { job: move.job, kind };
    if (move.machine) next.machine = move.machine;
    await queueMove(next);
  }

  async function reset(to: "plex" | "optimized") {
    const discarding = refine.pending_moves.length > 0 || refine.basis === "refined";
    if (discarding) {
      const ok = await confirm.confirm({
        title: to === "plex" ? "Reset to the Plex schedule?" : "Set to optimized?",
        detail:
          (refine.pending_moves.length > 0
            ? `This discards ${refine.pending_moves.length} pending move${refine.pending_moves.length === 1 ? "" : "s"}. `
            : "") +
          (refine.basis === "refined" ? "The replanned Refined plan is released; the standing plan is untouched." : ""),
        confirmLabel: to === "plex" ? "Reset to Plex schedule" : "Set to optimized",
      });
      if (!ok) return;
    }
    props.refine.reset(to);
  }

  const baselineLabel = standing ? `the standing plan v${standing.version}` : "the standing plan";

  return (
    <>
      {view ? (
        <>
          {props.refine.lastLanded && refineContext ? (
            <ReplanStrip landed={props.refine.lastLanded} baselineLabel={baselineLabel} onDismiss={props.refine.dismissLanded} />
          ) : null}

          {/* ── Lanes (§3.4; the rail lives on the Next-up tab) ──────────── */}
          <div className="card">
            <strong>Machine lanes</strong>
            <Lanes
              lanes={working.lanes}
              workingLabel={working.label}
              optimizedLanes={refineContext ? standing!.lanes : undefined}
              scheduledLanes={refineContext && refine.basis === "refined" && refine.refined ? refine.refined.scheduled_lanes : view.scheduled_lanes}
              measuredCounts={measuredCountsOf(view.changeover_classes)}
              calendarWindows={view.calendar_windows}
              expandedMachines={expandedMachines}
              onToggleMachine={(machine) => applyExpand(toggleMachine(expandState, viewMachines, machine))}
              onExpandAll={() => applyExpand({ all: true, machines: viewMachines })}
              onCollapseAll={() => applyExpand({ all: false, machines: [] })}
              highlightJobs={scenarioHighlight}
              onJobClick={refineContext ? (job, machine, anchor) => setPop({ job, machine, anchor, approved: job.approved_machines ?? null }) : undefined}
              pendingJobs={refineContext ? pendingJobs : undefined}
              findActiveJob={activeFind}
              findSlot={
                <FindBox
                  query={findQuery}
                  onQueryChange={(q) => { setFindQuery(q); setFindJob(null); }}
                  matches={matches}
                  position={findPosition}
                  onStep={(delta) => setFindJob(stepFind(matches, activeFind, delta))}
                  onClear={clearFind}
                />
              }
            />
          </div>

          {refineContext ? (
            <PendingMoves
              moves={refine.pending_moves}
              planJobs={workingPlanJobs}
              queuedBy={{ name: refine.updated_by_name, at: refine.updated_at }}
              busy={props.busy}
              onSwitchHow={(move, kind) => void switchHow(move, kind)}
              onEdit={(move, anchor, kind) => {
                const known = workingPlanJobs.get(move.job);
                setPop({ job: { job: move.job, part: known?.part ?? null }, machine: known?.machine ?? move.machine ?? "—", anchor, initialKind: kind, approved: known?.approved_machines ?? null });
              }}
              onRemove={(move) => void removeMove(move)}
            />
          ) : null}

          {/* ── The graded change sheet (Phase A) — below the lanes, one view ── */}
          <ChangeSheet sheet={working.sheet} target={working.target} />
        </>
      ) : (
        <div className="card">
          <EmptyState title="No plan to draw yet." />
        </div>
      )}

      {/* ── Who's out / What's down (§3.5) ──────────────────────────────── */}
      {canEdit ? (
        <div className="card">
          <strong>Who's out / What's down</strong>
          {scenario ? (
            <div className="help" style={{ marginTop: 4 }}>
              Entries added here are hypothetical — they shape only this scenario. The real entries above still apply.
            </div>
          ) : null}
          <DisruptionsPanel
            accepts={accepts}
            overlays={payload.overlays}
            machines={machines}
            planJobs={planJobs}
            busy={props.busy}
            mode={scenario ? "scenario" : "live"}
            scenarioEntries={props.scenarioEntries}
            onAssert={props.onAssertOverlay}
            onClear={props.onClearOverlay}
            onRemoveScenarioEntry={props.onRemoveScenarioEntry}
          />
        </div>
      ) : null}

      {refineContext ? (
        <RefineDock
          pendingCount={refine.pending_moves.length}
          basis={refine.basis}
          hasRefined={refine.basis === "refined" && !!refine.refined}
          busy={props.busy}
          inFlight={props.refine.inFlight}
          onReset={(to) => void reset(to)}
          onReplan={props.refine.replan}
          onPromote={props.refine.promote}
        />
      ) : null}

      {pop && refineContext ? (
        <MovePopover
          anchor={pop.anchor}
          job={pop.job}
          planMachine={pop.machine}
          machines={viewMachines}
          approved={pop.approved ?? workingPlanJobs.get(pop.job.job)?.approved_machines ?? null}
          bound={payload.eligibility?.bound === true}
          unavailableReason={payload.eligibility?.bound === true && payload.eligibility.available === false ? payload.eligibility.reason : null}
          planJobs={workingPlanJobs}
          basis={refine.basis}
          pending={refine.pending_moves}
          existing={refine.pending_moves.find((m) => m.job === pop.job.job) ?? null}
          initialKind={pop.initialKind}
          busy={props.busy}
          onQueue={queueMove}
          onClose={() => setPop(null)}
        />
      ) : null}
    </>
  );
}
